/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2021-2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
*                _
*  _            | | _
* | |         _ | || |
* | |__   _  | || || |__
* | '_ \ | | | || || '_ \
* | (_) || |_| || || (_) |
*  \___/  \___/ |_| \___/
*/

// Any place that we specifically add event listeners, we need to use this checklist to prevent the same
// event being added twice. Just mark it as true, and then proceed.
export const windowsEventListenerChecklist = {
  imageRotateAdded: false,
  imageCropAdded: false,
  deleteBlockAdded: false,
  removeCarouselImageAdded: false,
};

/**
 * Format the amount of bytes by a ceratin magnitude / factor.
 *
 * @param bytes Bytes to format.
 * @param decimals Order of magnitude / precision.
 * @returns {string}
 */
export function formatBytes(bytes, decimals = 2): string {
  if (bytes === 0) {
    return '0 KB';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Initialize the jwplayer if we need to, otherwise, just return it.
 * @returns {*}
 */
export function getJWPlayer() {
  // Storing the player id and key.
  const pid = (window as any).jwplayer?.defaults.pid;
  const key = (window as any).jwplayer?.defaults.key;

  // Then, set the default options for the jwplayer.
  (window as any).jwplayer.defaults = {
    autostart: false,
    controls: true,
    displaydescription: false,
    displaytitle: false,
    height: '100%',
    mute: false,
    ph: 1,
    primary: 'html5',
    repeat: false,
    stagevideo: false,
    stretching: 'uniform',
    visualplaylist: false,
    width: '100%',
    pid,
    key,
  };
  return (window as any).jwplayer;
}

/**
 * A helper function to change text into the title specific capitalization.
 */
export function toTitleCase(str) {
  if (str) {
    return str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
    );
  }
  return undefined;
}

/**
 * Calling this method make sure that all the iframe with pdf embed are loaded.
 */
export function loadIframeWithPDFEmbed(): void {
  let allIframeWithPDFEmbedLoaded = true;
  // Get all the iFrames.
  const iFrames = document.getElementsByTagName('iframe');
  angular.forEach(iFrames, (iFrame) => {
    try {
      // Only checking if it is a google viewer.
      if (iFrame
        && iFrame.src.indexOf('https://docs.google.com/viewer') !== -1 && iFrame.contentWindow.document) {
        // iFrame.contentWindow.document throw cross origin exception if iFrame content is loaded successfully.
        // if exception is not throw, then the iFrame content is not loaded.
        // In this case set the flag to false so that we can retry loading.
        allIframeWithPDFEmbedLoaded = false;
        iFrame.src += '';
      }
    } catch (e) {
      // Do nothing because Iframe content is Already loaded
    }
  });
  // try again after 3 seconds if content of any pdf embed iframe is not loaded.
  if (!allIframeWithPDFEmbedLoaded) {
    setTimeout(() => {
      loadIframeWithPDFEmbed();
    }, 3000);
  }
}

/**
 * Updates the `src` attributes of all `<iframe>` elements.
 */
export function updateIframeSrcAttributes(): void {
  const iFrames = document.getElementsByTagName('iframe');
  angular.forEach(iFrames, (iFrame) => {
    const src = iFrame.getAttribute('src');
    if (src && !src.includes('bulbapp.com')) {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('rel', 'nofollow ugc');
      iFrame.setAttribute('src', url.toString());
    }
  });
}

/**
 * Add the provided HTML template to the view within the indicated parent node.
 * The elements in the template will be added after any existing children of the parent.
 *
 * Example:
 *     const parent = writer.createContainerElement( 'div', {} );
 *     let html = '<div><span>hello</span></div>';
 *     addTemplateToView(writer, parent, html);
 *
 * @param writer downcastWriter to use to write the template elements to the view.
 * @param parent Parent view element to host the template elements.
 * @param templateHtml Valid HTML string to be parsed into elements and added to the view.
 */
// export function addTemplateToView(writer, parent, templateHtml) {
//   // Creating a standard javascript DOM so we can leverage the built-in html parsing ability:
//   const document = new Document();
//   const template = document.createElement('template');
//   template.innerHTML = templateHtml;
//
//   // We intentionally don't add the 'template' node itself, because it is just a wrapper of the html which is the
//   // content we really want to add to the view.
//   addNodeChildrenToView(writer, parent, template);
// }

/**
 * Add children of provided DOM node to the parent view node.
 * Children will be added after any existing children of the parent view node.
 *
 * @param writer downcastWriter to use to write the template elements to the view.
 * @param parent Parent view element to host the node's children.
 * @param node DOM node whose children should be added as children to the parent view node.
 */
// function addNodeChildrenToView(writer, parent, node) {
//   const emptyElements = ['br', 'hr', 'img'];
//   node.childNodes.forEach((child) => {
//     if (child.nodeName === '#text') {
//       const textNode = writer.createText(child.data);
//       writer.insert(writer.createPositionAt(parent, 'end'), textNode);
//     } else {
//       const attributes = getAttributesForNode(child);
//       if (emptyElements.indexOf(child.nodeName) !== -1) {
//         const emptyNode = writer.createEmptyElement(child.nodeName, attributes);
//         writer.insert(writer.createPositionAt(parent, 'end'), emptyNode);
//       } else {
//         const containerNode = writer.createContainerElement(child.nodeName, attributes);
//         writer.insert(writer.createPositionAt(parent, 'end'), containerNode);
//         addNodeChildrenToView(writer, containerNode, child);
//       }
//     }
//   });
// }

/**
 * Extract all attributes from the provided DOM node and returns a normal javascript object representing them.
 *
 * @param node DOM node whose attributes should be returned.
 * @returns {Object} The attribute keys and values.
 */
// function getAttributesForNode(node) {
//   const attributes = {};
//   if (node.attributes) {
//     for (let i = 0; i < node.attributes.length; i++) {
//       attributes[node.attributes[i].name] = node.attributes[i].value;
//     }
//   }
//   return attributes;
// }

/**
 * Set Overlay visibility on videos.
 * @param event Click event
 */
export function setOverlay(event) {
  const { editor } = event.currentTarget;
  if (event.target.classList.contains('overlay')
    || (event.target.nodeName !== 'VIDEO' && !event.target.closest('.bulb_jwplayer_wrapper'))) {
    const block = editor.model.document.selection.getSelectedElement();
    const isOverlayElement = event.target.classList.contains('overlay');
    // contentBlockId of the selected overlay.
    const contentBlockId = block && isOverlayElement ? block.getAttribute('bulb-block-id') : null;
    const ckeditor = document.getElementsByClassName('bulb-page-content')[0];
    if (ckeditor) {
      const overlayElements = Array.from(ckeditor.querySelectorAll('.overlay')) as HTMLElement[];
      overlayElements.forEach((element) => {
        if (element.id === `overlay-${contentBlockId}`) {
          element.style.display = 'none';
        } else {
          element.style.display = 'block';
        }
      });
    }
  }
}

/**
 * Get version of block.
 *
 * @param bulb Object which contains required data to use within cke
 * @param element Element of block
 * @return {number|*} Version of block
 */
export function getBlockVersion(bulb, element) {
  // Navigating from a CKE page to bulb home or any other non page view we get "bulb.page" as an empty object
  // therefore adding this check.
  if (!Object.keys(bulb.page).length) {
    return -1;
  }
  if (bulb.page && !bulb.page.unit) {
    bulb.service.error.handleException('Error loading cke page which unit is null');
    return -1;
  }
  if (bulb.page.unit.contentBlocks) {
    const contentBlock = bulb.page.unit.contentBlocks[element.getAttribute('bulb-block-id')];
    if (contentBlock) {
      return contentBlock.version;
    }
  }
  return 0;
}

/**
 * It will insert a empty paragraph above for asset insertion.
 * @param editor Editor instance
 */
export function assetInsertLocation(editor) {
  const selectedBlock = editor.model.document.selection.getSelectedElement()
    || Array.from(editor.model.document.selection.getSelectedBlocks())[0];

  const range = editor.model.document.selection.getFirstRange();
  let selectedText = '';
  for (const item of range.getItems()) {
    selectedText = item.data;
  }
  if (selectedBlock && selectedText !== '') {
    editor.execute('insertParagraph', {
      position: editor.model.createPositionAt(selectedBlock, 'before'),
    });
    CKEBulbData.service.ckeStateService.insertParagraphSubject.next(false);
    const editingView = editor.editing.view;
    editingView.focus();
  }
}

/**
 *
 * It will stop the busy indicator.
 * @param editor Editor instance.
 */
export function cancelBusyIndicator(editor) {
  // Timeout is needed to fullfill the gap between asset upload and transcoding.
  setTimeout(() => {
    const stopBusyIndicator = CKEBulbData.service.ckeStateService.stopBusyIndicator.value;
    if (stopBusyIndicator) {
      stopBusyIndicator();
      CKEBulbData.service.ckeStateService.stopBusyIndicator.next(null);
    }
  }, 3000);
}
